import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import {
  CAMPAIGN_STATUSES,
  invalidateCampaignQueries,
} from '#/entities/campaign'
import type { CampaignStatus } from '#/entities/campaign'
import {
  useDuplicateCampaignApiV1CampaignsCampaignIdDuplicatePost,
  usePauseCampaignApiV1CampaignsCampaignIdPausePost,
  useStartCampaignApiV1CampaignsCampaignIdStartPost,
} from '#/shared/api/generated/communication/theCargoCommunication'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { toast } from '#/shared/ui/sonner'

import { CAMPAIGN_DETAIL_DEFAULT_TAB } from './detail-tabs'

/** Which way the one lifecycle button in the header points. */
export const CAMPAIGN_LIFECYCLE_ACTIONS = {
  pause: 'pause',
  resume: 'resume',
  start: 'start',
} as const

export type CampaignLifecycleAction =
  (typeof CAMPAIGN_LIFECYCLE_ACTIONS)[keyof typeof CAMPAIGN_LIFECYCLE_ACTIONS]

/**
 * `running → pause`, `paused → resume`, `draft → start`; a scheduled campaign
 * is already on its way and a stopped or finished one is over, so those show
 * nothing. Resume and start are the same call — the service's `start` picks a
 * paused sequence up where it left off.
 */
export function campaignLifecycleAction(
  status: CampaignStatus,
): CampaignLifecycleAction | null {
  switch (status) {
    case CAMPAIGN_STATUSES.running:
      return CAMPAIGN_LIFECYCLE_ACTIONS.pause
    case CAMPAIGN_STATUSES.paused:
      return CAMPAIGN_LIFECYCLE_ACTIONS.resume
    case CAMPAIGN_STATUSES.draft:
      return CAMPAIGN_LIFECYCLE_ACTIONS.start
    default:
      return null
  }
}

export interface UseCampaignActionsResult {
  onLifecycle: (action: CampaignLifecycleAction) => void
  onDuplicate: () => void
  isBusy: boolean
}

export function useCampaignActions(
  campaignId: string,
): UseCampaignActionsResult {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const fail = useCallback(
    (error: unknown) =>
      toast.error(
        getApiErrorDetail(error) ?? t('campaigns.detail.actionError'),
      ),
    [t],
  )
  const settle = useCallback(
    () => invalidateCampaignQueries(queryClient),
    [queryClient],
  )

  const pause = usePauseCampaignApiV1CampaignsCampaignIdPausePost({
    mutation: {
      onSuccess: () => {
        toast.success(t('campaigns.detail.toasts.paused'))
        settle()
      },
      onError: fail,
    },
  })
  const start = useStartCampaignApiV1CampaignsCampaignIdStartPost({
    mutation: {
      onSuccess: () => {
        toast.success(t('campaigns.detail.toasts.started'))
        settle()
      },
      onError: fail,
    },
  })
  const duplicate = useDuplicateCampaignApiV1CampaignsCampaignIdDuplicatePost({
    mutation: {
      onSuccess: (response) => {
        toast.success(t('campaigns.detail.toasts.duplicated'))
        settle()
        void navigate({
          to: '/campaign/$campaignId',
          params: { campaignId: response.data.id },
          search: { tab: CAMPAIGN_DETAIL_DEFAULT_TAB },
        })
      },
      onError: fail,
    },
  })

  const onLifecycle = useCallback(
    (action: CampaignLifecycleAction) => {
      if (action === CAMPAIGN_LIFECYCLE_ACTIONS.pause) {
        pause.mutate({ campaignId })
      } else {
        start.mutate({ campaignId, data: null })
      }
    },
    [campaignId, pause, start],
  )

  return {
    onLifecycle,
    onDuplicate: () => duplicate.mutate({ campaignId }),
    isBusy: pause.isPending || start.isPending || duplicate.isPending,
  }
}
